import React, { useEffect, useMemo, useState } from 'react';
import { useMountEffect } from './hooks';

export type DesignSystemTheme = 'timely-light';
const ThemeContext = React.createContext<{
  theme: DesignSystemTheme;
}>({
  theme: 'timely-light',
});
ThemeContext.displayName = 'ThemeContext';

interface ThemeProviderProps {
  theme: DesignSystemTheme;
  children: React.ReactNode;
}
export const ThemeProvider: React.FC<ThemeProviderProps> = ({ theme, children }) => {
  useThemeBodyClass(theme);
  return <ThemeContext.Provider value={{ theme }}>{children}</ThemeContext.Provider>;
};
ThemeProvider.displayName = 'ThemeProvider';

const useThemeBodyClass = (theme: DesignSystemTheme) => {
  useEffect(() => {
    Array.from(document.documentElement.classList).forEach(cls => {
      if (cls.startsWith('theme-')) {
        document.documentElement.classList.remove(cls);
      }
    });
    document.documentElement.classList.add(`theme-${theme}`);
  }, [theme]);
};

export const useCurrentTheme = () => {
  const { theme } = React.useContext(ThemeContext);
  return theme;
};
useCurrentTheme.displayName = 'useCurrentTheme';

export const AutoThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const darkModeQuery = useMemo(
    () =>
      typeof window !== 'undefined' ? window.matchMedia('(prefers-color-scheme: dark)') : null,
    []
  );
  const [, setDarkMode] = useState(() => !!darkModeQuery?.matches);
  useMountEffect(() => {
    darkModeQuery?.addEventListener('change', () => {
      setDarkMode(darkModeQuery.matches);
    });
  });
  const theme: DesignSystemTheme = 'timely-light';
  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};
AutoThemeProvider.displayName = 'AutoThemeProvider';
